/**
 * Workspace settings (spec §8.22, M19): members and their roles, the workspace's policy, its name,
 * and the pages that carry a write restriction. Every member may open it; for anyone but an owner
 * the controls are disabled (Q52). Switching auto-extraction or LLM egress *on* asks first (Q53).
 * [M20] An owner deletes the workspace here, typing its name to confirm (spec §13.10).
 */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useState } from "react";
import { useAddMember, useChangeRole, useDeleteWorkspace, useMe, useMembers, usePages, useRemoveMember, useUpdateWorkspace, useWorkspace } from "../api/hooks";
import { setDeletedWorkspace } from "../lib/deletedWorkspace";
import { ApiError } from "../api/client";
import type { Role, Workspace } from "../api/types";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Rich, useT } from "../i18n";
import { roleKey } from "../i18n/roles";

const ROLES: Role[] = ["owner", "editor", "viewer"];

export function WorkspaceSettingsPage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const t = useT();
  const ws = useWorkspace(workspaceId);
  const me = useMe();
  const update = useUpdateWorkspace(workspaceId);
  const [error, setError] = useState<string | null>(null);
  const owner = ws.data?.my_role === "owner";

  const save = (patch: Parameters<typeof update.mutate>[0]) => {
    setError(null);
    update.mutate(patch, { onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)) });
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-9 pb-16 pt-8" data-testid="workspace-settings">
      <header className="mb-6 flex items-center gap-3">
        <Link
          to="/w/$workspaceId"
          params={{ workspaceId }}
          className="rounded-sm px-2.5 py-1.5 text-[13.5px] text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          {t("page.backToWorkspace")}
        </Link>
        <h1 className="text-[26px] font-semibold">{t("settings.title")}</h1>
      </header>

      {!owner && ws.data && (
        <p className="mb-4 text-sm text-neutral-500" data-testid="settings-read-only">
          {t("settings.readOnly")}
        </p>
      )}
      {error && (
        <p className="mb-4 rounded-sm bg-err-tint px-3 py-2 text-sm text-err" role="alert" data-testid="settings-error">
          {error}
        </p>
      )}

      <Name workspace={ws.data} owner={owner} onSave={(name) => save({ name })} />
      <Members workspaceId={workspaceId} owner={owner} meId={me.data?.id} oidcManaged={!!ws.data?.oidc_managed} onError={setError} />
      <Policy workspace={ws.data} owner={owner} onSave={save} />
      <RestrictedPages workspaceId={workspaceId} />

      <DeleteWorkspace workspace={ws.data} owner={owner} />
    </div>
  );
}

function DeleteWorkspace({ workspace, owner }: { workspace?: Workspace; owner: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const remove = useDeleteWorkspace(workspace?.id ?? "");
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!workspace) return null;
  const close = () => {
    setOpen(false);
    setTyped("");
    setError(null);
  };
  return (
    <section className="kss-surface mt-4 p-4 text-sm" data-testid="settings-delete">
      <h2 className="mb-1 text-[17px] font-semibold">{t("settings.delete.title")}</h2>
      <p className="mb-3 text-neutral-500">{owner ? t("settings.delete.body") : t("settings.delete.ownerOnly")}</p>
      {owner && (
        <Button variant="danger" onClick={() => setOpen(true)} data-testid="settings-delete-open">
          <Trash2 size={15} aria-hidden="true" />
          {t("settings.delete.button")}
        </Button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={close}>
          <form
            className="w-full max-w-md rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("settings.delete.title")}
            data-testid="delete-workspace-dialog"
            onSubmit={(e) => {
              e.preventDefault();
              if (typed.trim() !== workspace.name) return;
              remove.mutate(typed.trim(), {
                onSuccess: () => {
                  setDeletedWorkspace({ id: workspace.id, name: workspace.name });
                  void navigate({ to: "/" });
                },
                onError: (err) => setError(err instanceof ApiError ? err.detail : String(err)),
              });
            }}
          >
            <h2 className="mb-1 text-[17px] font-semibold">{t("settings.delete.confirm.title", { name: workspace.name })}</h2>
            <p className="mb-3 text-neutral-500">
              <Rich strong="font-medium text-neutral-900 dark:text-neutral-100" text={t("settings.delete.confirm.body", { name: workspace.name })} />
            </p>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              aria-label={t("settings.delete.confirm.label")}
              placeholder={workspace.name}
              data-testid="delete-workspace-name"
            />
            {error && (
              <p className="mt-2 text-err" role="alert">
                {error}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="danger" disabled={typed.trim() !== workspace.name || remove.isPending} data-testid="delete-workspace-confirm">
                {t("settings.delete.confirm.ok")}
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

function Card({ title, sub, children, testId }: { title: string; sub?: string; children: React.ReactNode; testId?: string }) {
  return (
    <section className="kss-surface mb-4 p-4" data-testid={testId}>
      <h2 className="text-[17px] font-semibold">{title}</h2>
      {sub && <p className="mb-3 text-[13px] text-neutral-500">{sub}</p>}
      {children}
    </section>
  );
}

function Name({ workspace, owner, onSave }: { workspace?: Workspace; owner: boolean; onSave(name: string): void }) {
  const t = useT();
  const [name, setName] = useState<string | null>(null);
  const value = name ?? workspace?.name ?? "";
  const dirty = name !== null && name.trim() !== workspace?.name;
  return (
    <Card title={t("settings.name")} testId="settings-name">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty) onSave(value.trim());
          setName(null);
        }}
      >
        <Input value={value} disabled={!owner} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" />
        <Button type="submit" disabled={!owner || !dirty || !value.trim()} data-testid="settings-name-save">
          {t("settings.rename")}
        </Button>
      </form>
    </Card>
  );
}

function Members({
  workspaceId,
  owner,
  meId,
  oidcManaged,
  onError,
}: {
  workspaceId: string;
  owner: boolean;
  meId?: string;
  oidcManaged: boolean;
  onError(message: string): void;
}) {
  const t = useT();
  const members = useMembers(workspaceId);
  const add = useAddMember(workspaceId);
  const changeRole = useChangeRole(workspaceId);
  const remove = useRemoveMember(workspaceId);
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<Role>("editor");
  // [0.57] why adding failed, next to the field (spec §8.22): the server answers with a code
  const [addError, setAddError] = useState<string | null>(null);
  const fail = (e: unknown) => onError(e instanceof ApiError ? e.detail : String(e));
  const ADD_ERRORS: Record<string, string> = {
    not_found: t("settings.members.notFound"),
    ambiguous: t("settings.members.ambiguous"),
    already_member: t("settings.members.already"),
  };

  return (
    <Card title={t("settings.members")} sub={t("settings.members.sub")} testId="settings-members">
      {oidcManaged && (
        <p className="mb-3 flex items-start gap-2 rounded-sm bg-warn-tint px-3 py-2 text-[13px] text-warn" data-testid="settings-oidc-notice">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <Rich text={t("settings.members.oidc")} strong="font-medium italic" />
          </span>
        </p>
      )}
      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800" data-testid="member-list">
        {members.data?.map((m) => (
          <li key={m.user.id} className="flex items-center gap-2 py-2" data-testid="member-row" data-username={m.user.username}>
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: m.user.color }} />
            <span className="min-w-0 grow truncate">
              {m.user.display_name || m.user.username}
              <span className="ml-1 text-xs text-neutral-500">{m.user.username}</span>
              {m.user.keycloak_username && m.user.keycloak_username !== m.user.username && (
                <span className="ml-1 text-xs text-neutral-500" data-testid="member-keycloak">
                  {t("settings.members.keycloak", { name: m.user.keycloak_username })}
                </span>
              )}
              {m.user.id === meId && <span className="ml-1 text-xs text-neutral-500">{t("settings.members.you")}</span>}
            </span>
            {m.source === "oidc" && <span className="kss-pill border-neutral-200 text-neutral-500 dark:border-neutral-700">oidc</span>}
            <select
              className="kss-control kss-field bg-transparent px-2 py-1 text-xs"
              value={m.role}
              disabled={!owner}
              aria-label={t("settings.members.roleOf", { user: m.user.username })}
              data-testid="member-role"
              onChange={(e) => changeRole.mutate({ userId: m.user.id, role: e.target.value as Role }, { onError: fail })}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(roleKey(r))}
                </option>
              ))}
            </select>
            {owner && (
              <button
                className="text-neutral-400 transition hover:text-err"
                title={t("settings.members.remove", { user: m.user.username })}
                aria-label={t("settings.members.remove", { user: m.user.username })}
                data-testid="member-remove"
                onClick={() => remove.mutate(m.user.id, { onError: fail })}
              >
                <Trash2 size={14} />
              </button>
            )}
          </li>
        ))}
        {members.isError && <li className="py-2 text-neutral-500">{t("settings.members.needServer")}</li>}
      </ul>
      {owner && (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!username.trim()) return;
            setAddError(null);
            add.mutate(
              { username: username.trim(), role },
              {
                onSuccess: () => setUsername(""),
                onError: (e) => {
                  const code = e instanceof ApiError ? e.detail : "";
                  if (code in ADD_ERRORS) setAddError(ADD_ERRORS[code]!);
                  else fail(e);
                },
              },
            );
          }}
        >
          <Input
            placeholder={t("settings.members.who")}
            aria-label={t("settings.members.who")}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setAddError(null);
            }}
            data-testid="member-username"
          />
          <select
            className="kss-control kss-field bg-transparent px-2 py-1 text-sm"
            value={role}
            aria-label={t("settings.members.newRole")}
            data-testid="member-new-role"
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(roleKey(r))}
              </option>
            ))}
          </select>
          <Button type="submit" disabled={!username.trim()} data-testid="member-add">
            {t("settings.members.add")}
          </Button>
        </form>
      )}
      {owner && addError && (
        <p className="mt-2 text-sm text-err" role="alert" data-testid="member-add-error">
          {addError}
        </p>
      )}
    </Card>
  );
}

function Policy({ workspace, owner, onSave }: { workspace?: Workspace; owner: boolean; onSave(patch: { auto_extract?: "default" | "on" | "off"; llm_egress_allowed?: boolean; web_browsing_allowed?: boolean; mcp_allowed?: boolean; ontology_auto_migrate?: boolean }): void }) {
  const t = useT();
  const [confirm, setConfirm] = useState<null | { what: "auto_extract" | "llm_egress_allowed"; apply(): void }>(null);
  if (!workspace) return null;
  const host = workspace.llm_host ?? t("settings.policy.endpoint");
  const model = workspace.llm_model ? ` (${workspace.llm_model})` : "";
  const autoValue = workspace.auto_extract === null || workspace.auto_extract === undefined ? "default" : workspace.auto_extract ? "on" : "off";

  return (
    <Card title={t("settings.policy")} testId="settings-policy">
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <select
            className="kss-control kss-field w-36 shrink-0 bg-transparent px-2 py-1 text-xs"
            value={autoValue}
            disabled={!owner}
            aria-label={t("settings.policy.auto")}
            data-testid="policy-auto-extract"
            onChange={(e) => {
              const next = e.target.value as "default" | "on" | "off";
              const turnsOn = next === "on" || (next === "default" && workspace.auto_extract_effective === false && workspace.auto_extract === false);
              if (next === "on" || turnsOn) setConfirm({ what: "auto_extract", apply: () => onSave({ auto_extract: next }) });
              else onSave({ auto_extract: next });
            }}
          >
            <option value="default">{t("settings.policy.serverDefault")}</option>
            <option value="on">{t("settings.policy.on")}</option>
            <option value="off">{t("settings.policy.off")}</option>
          </select>
          <span>
            <strong className="font-medium">{t("settings.policy.auto")}.</strong>{" "}
            <span className="text-neutral-500">
              {t("settings.policy.auto.body", { state: t(workspace.auto_extract_effective ? "settings.policy.on" : "settings.policy.off") })}
            </span>
          </span>
        </div>

        <Toggle
          testId="policy-llm-egress"
          label={t("settings.policy.egress")}
          checked={!!workspace.llm_egress_allowed}
          disabled={!owner}
          onChange={(next) =>
            next
              ? setConfirm({ what: "llm_egress_allowed", apply: () => onSave({ llm_egress_allowed: true }) })
              : onSave({ llm_egress_allowed: false })
          }
        >
          {t("settings.policy.egress.body", { host, model })}
        </Toggle>

        <Toggle
          testId="policy-web-browsing"
          label={t("settings.policy.web")}
          checked={workspace.web_browsing_allowed !== false}
          disabled={!owner}
          onChange={(next) => onSave({ web_browsing_allowed: next })}
        >
          {t("settings.policy.web.body")}
        </Toggle>

        <Toggle
          testId="policy-mcp"
          label={t("settings.policy.mcp")}
          checked={workspace.mcp_allowed !== false}
          disabled={!owner}
          onChange={(next) => onSave({ mcp_allowed: next })}
        >
          {t("settings.policy.mcp.body")}
        </Toggle>

        {/* [M33] automatic ontology migrations (spec §14.8, Q94) */}
        <Toggle
          testId="policy-auto-migrate"
          label={t("settings.policy.migrate")}
          checked={workspace.ontology_auto_migrate !== false}
          disabled={!owner}
          onChange={(next) => onSave({ ontology_auto_migrate: next })}
        >
          {t("settings.policy.migrate.body")}
        </Toggle>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={() => setConfirm(null)}>
          <div
            className="w-full max-w-md rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("settings.confirm")}
            data-testid="policy-confirm"
          >
            <h2 className="mb-1 text-[17px] font-semibold">
              {confirm.what === "llm_egress_allowed" ? t("settings.confirm.egress.title") : t("settings.confirm.auto.title")}
            </h2>
            <p className="mb-4 text-neutral-500">
              <Rich
                strong="font-medium text-neutral-900 dark:text-neutral-100"
                text={
                  confirm.what === "llm_egress_allowed"
                    ? t("settings.confirm.egress.body", {
                        host,
                        model,
                        notListed: workspace.llm_host_allow_listed === false ? t("settings.confirm.notListed") : "",
                      })
                    : t("settings.confirm.auto.body", { host, model })
                }
              />
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(null)} data-testid="policy-confirm-cancel">
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => {
                  confirm.apply();
                  setConfirm(null);
                }}
                data-testid="policy-confirm-ok"
              >
                {t("settings.confirm.allow")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function Toggle({
  label,
  checked,
  disabled,
  onChange,
  children,
  testId,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange(next: boolean): void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
        checked={checked}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <strong className="font-medium">{label}.</strong> <span className="text-neutral-500">{children}</span>
      </span>
    </label>
  );
}

/** Pages whose writing is restricted for someone; editing happens on the page itself (Q50). */
function RestrictedPages({ workspaceId }: { workspaceId: string }) {
  const t = useT();
  const pages = usePages(workspaceId);
  const restricted = (pages.data ?? []).filter((p) => p.restricted);
  return (
    <Card
      title={t("settings.restricted")}
      sub={t("settings.restricted.sub")}
      testId="settings-restricted"
    >
      <ul className="space-y-1 text-sm" data-testid="restricted-pages">
        {restricted.map((p) => (
          <li key={p.id}>
            <Link to="/w/$workspaceId/d/$docId" params={{ workspaceId, docId: p.id }} className="text-accent hover:underline">
              {p.title || t("page.untitled")}
            </Link>
            {p.my_mode === "read" && <span className="ml-1 text-xs text-neutral-500">{t("settings.restricted.forYou")}</span>}
          </li>
        ))}
        {restricted.length === 0 && <li className="text-neutral-500">{t("settings.restricted.none")}</li>}
      </ul>
    </Card>
  );
}
